import noUiSlider from 'nouislider'
import { formatMoney } from '@archetype-themes/utils/currency'

if (typeof noUiSlider === 'undefined') {
  throw new Error('theme.PriceRange is missing vendor noUiSlider: // =require vendor/nouislider.js')
}

const defaultStep = 0.01
const selectors = {
  priceRange: '.price-range',
  priceRangeSlider: '.price-range__slider',
  priceRangeInputMin: '.price-range__input-min',
  priceRangeInputMax: '.price-range__input-max',
  priceRangeDisplayMin: '.price-range__display-min',
  priceRangeDisplayMax: '.price-range__display-max'
}

class PriceRange extends HTMLElement {
  connectedCallback() {
    this.init()
  }

  init() {
    if (!this.classList.contains('price-range')) {
      throw new Error('You must instantiate PriceRange with a valid container')
    }

    this.formEl = this.closest('form')
    this.sliderEl = this.querySelector(selectors.priceRangeSlider)
    this.inputMinEl = this.querySelector(selectors.priceRangeInputMin)
    this.inputMaxEl = this.querySelector(selectors.priceRangeInputMax)
    this.displayMinEl = this.querySelector(selectors.priceRangeDisplayMin)
    this.displayMaxEl = this.querySelector(selectors.priceRangeDisplayMax)
    this.moneyFormat = this.dataset.moneyFormat
    this.superScript = this.dataset.superScript === 'true'

    this.minRange = parseFloat(this.dataset.min) || 0
    this.minValue = parseFloat(this.dataset.minValue) || 0
    this.maxRange = parseFloat(this.dataset.max) || 100
    this.maxValue = parseFloat(this.dataset.maxValue) || this.maxRange

    return this.createPriceRange()
  }

  createPriceRange() {
    if (this.sliderEl && this.sliderEl.noUiSlider && typeof this.sliderEl.noUiSlider.destroy === 'function') {
      this.sliderEl.noUiSlider.destroy()
    }

    const slider = noUiSlider.create(this.sliderEl, {
      connect: true,
      step: defaultStep,
      // Do not allow overriding these options
      start: [this.minValue, this.maxValue],
      range: {
        min: this.minRange,
        max: this.maxRange
      }
    })

    slider.on('update', (values) => {
      if (this.displayMinEl) {
        this.displayMinEl.innerHTML = formatMoney(values[0], this.moneyFormat, this.superScript)
      }
      if (this.displayMaxEl) {
        this.displayMaxEl.innerHTML = formatMoney(values[1], this.moneyFormat, this.superScript)
      }
      
      // Only update input fields if we're not currently typing in them
      if (!this._updatingFromInputs && !document.activeElement.matches('.price-range__input')) {
        this._updatingFromSlider = true
        this.inputMinEl.value = parseFloat(values[0]).toFixed(2)
        this.inputMaxEl.value = parseFloat(values[1]).toFixed(2)
        this._updatingFromSlider = false
      }

      /**
       * @event price-range:update
       * @description Triggered when the price range slider is updated.
       * @param {object} detail - The values of the price range slider.
       */
      this.dispatchEvent(
        new CustomEvent('price-range:update', {
          bubbles: true,
          detail: values
        })
      )
    })

    slider.on('change', (values) => {
      // Only update inputs if we're not currently updating from inputs
      if (!this._updatingFromInputs) {
        this._updatingFromSlider = true
        this.inputMinEl.value = parseFloat(values[0]).toFixed(2)
        this.inputMaxEl.value = parseFloat(values[1]).toFixed(2)
        this._updatingFromSlider = false
      }

      const formData = new FormData(this.formEl)

      /**
       * @event price-range:change
       * @description Triggered when the price range slider is changed.
       * @param {Object} formData - The form data.
       */
      this.dispatchEvent(
        new CustomEvent('price-range:change', {
          bubbles: true,
          detail: formData
        })
      )
    })

    // Add event listeners for input fields to update slider
    if (this.inputMinEl) {
      // Handle change events when user finishes editing
      this.inputMinEl.addEventListener('change', (e) => {
        this.handleInputChange(slider)
        this.dispatchChangeEvent()
      })
      
      // Handle input events for real-time updates
      this.inputMinEl.addEventListener('input', (e) => {
        // Use debouncing to avoid too many updates
        clearTimeout(this._minInputTimeout)
        this._minInputTimeout = setTimeout(() => {
          this.handleInputChange(slider)
        }, 300)
      })
    }

    if (this.inputMaxEl) {
      // Handle change events when user finishes editing
      this.inputMaxEl.addEventListener('change', (e) => {
        this.handleInputChange(slider)
        this.dispatchChangeEvent()
      })
      
      // Handle input events for real-time updates
      this.inputMaxEl.addEventListener('input', (e) => {
        // Use debouncing to avoid too many updates
        clearTimeout(this._maxInputTimeout)
        this._maxInputTimeout = setTimeout(() => {
          this.handleInputChange(slider)
        }, 300)
      })
    }

    return slider
  }

  handleInputChange(slider) {
    // Prevent loops when slider is updating inputs
    if (this._updatingFromSlider) {
      return
    }

    // Get current values from inputs - don't provide fallbacks to allow empty values
    const minInputValue = this.inputMinEl.value
    const maxInputValue = this.inputMaxEl.value
    
    // Only proceed if we have valid numbers
    const minValue = parseFloat(minInputValue)
    const maxValue = parseFloat(maxInputValue)
    
    // If either input is empty or invalid, don't update the slider
    if (isNaN(minValue) || isNaN(maxValue)) {
      return
    }

    // Set flag to indicate we're updating from inputs
    this._updatingFromInputs = true

    // Ensure values are within valid range
    const clampedMin = Math.max(this.minRange, Math.min(minValue, this.maxRange))
    const clampedMax = Math.max(this.minRange, Math.min(maxValue, this.maxRange))

    // Ensure min is not greater than max
    const finalMin = Math.min(clampedMin, clampedMax)
    const finalMax = Math.max(clampedMin, clampedMax)

    // Update slider
    if (slider && slider.set) {
      slider.set([finalMin, finalMax])
    }

    // Clear the flag after a short delay to allow slider events to complete
    setTimeout(() => {
      this._updatingFromInputs = false
    }, 50)
  }

  updateSliderFromInputs(slider) {
    const minValue = parseFloat(this.inputMinEl.value) || this.minRange
    const maxValue = parseFloat(this.inputMaxEl.value) || this.maxRange

    // Ensure values are within valid range
    const clampedMin = Math.max(this.minRange, Math.min(minValue, this.maxRange))
    const clampedMax = Math.max(this.minRange, Math.min(maxValue, this.maxRange))

    // Ensure min is not greater than max
    const finalMin = Math.min(clampedMin, clampedMax)
    const finalMax = Math.max(clampedMin, clampedMax)

    // Update slider
    slider.set([finalMin, finalMax])
  }

  dispatchChangeEvent() {
    const formData = new FormData(this.formEl)
    this.dispatchEvent(
      new CustomEvent('price-range:change', {
        bubbles: true,
        detail: formData
      })
    )
  }
}

customElements.define('price-range', PriceRange)
